import React, { useEffect, useState } from "react";
import { Typography, IconButton, Badge, Button } from "@mui/material";
import Box from "@mui/material/Box";
import { getIcon } from "utils/iconsConfig";
import ImageModal from "./ImageModal";
import Tooltip from "./Tooltip";
import SmallImage from "./SmallImage";
import { useLocation, useNavigate } from "react-router-dom";
import { findSpecificMenu, findSpecificPage, findSpecificSubMenu, findSpecificSubPage, hasAccessSubPage, isAllowed } from "utils/checkUserRigths";
import { usePermissions } from "context/PermissionsContext";
import { formatDate } from "utils/formatDate";
import { useFilter } from "context/FilterContext";
import { resolveIcon } from "./ProductCard";

const StockSetProductCard = ({ data, btnTilte, isBtn, hoverEffect, variant = "default", handleViewCartDetails, btnWidth, addtoCart, selectedCartProducts, removeCart, presentInCatalog, allData }) => {
  const [details, setDetails] = useState([]);
  const [imageModal, setImageModal] = useState({ isOpen: false, imgUrl: null, designNo: null, selectedCartProduct: null });
  const [currentIndex, setCurrentIndex] = useState(null);
  const { filterValues } = useFilter();
  const isFirstItem = currentIndex === 0;
  const isLastItem = currentIndex === allData?.length - 1 || allData?.length === 0;

  const location = useLocation();
  const navigate = useNavigate();
  const Location = filterValues?.Location?.map((location) => (location === "IDL" ? "Idel Inventory" : location)) || [];

  const stockStatus = filterValues["Stock Status"]?.[0] === "On Memo" ? "Memo" : filterValues["Stock Status"]?.[0] || "";
  const permission = usePermissions();

  // const hasAccessToSeeWatermark = hasAccessSubPage(findSpecificSubPage(findSpecificPage("Other Settings", permission), "Watermark"), "view");
  const hasAccessToSeeWatermark = isAllowed(findSpecificSubMenu(findSpecificMenu("Vision Web", permission), "Watermark"), "view") && !permission?.isAdmin;;
  const resolveProductByStyleNumber = React.useCallback(
    (styleNumber) => {
      const productFromList = allData?.find((item) => item?.styleNumber === styleNumber);
      if (productFromList) return productFromList;
      if (selectedCartProducts?.styleNumber === styleNumber) return selectedCartProducts;
      if (data?.styleNumber === styleNumber) return data;
      return selectedCartProducts || data || {};
    },
    [allData, selectedCartProducts, data]
  );

  const resolvedCartProduct = React.useMemo(() => {
    if (selectedCartProducts && Object.keys(selectedCartProducts).length > 1) return selectedCartProducts;
    return resolveProductByStyleNumber(data?.styleNumber);
  }, [selectedCartProducts, data?.styleNumber, resolveProductByStyleNumber]);

  const handleOpenImageModal = React.useCallback(
    (imgUrl, designNo) => {
      setImageModal({
        isOpen: true,
        imgUrl,
        designNo,
        selectedCartProduct: resolveProductByStyleNumber(designNo),
      });
      let newIndex = allData?.findIndex((item) => item?.styleNumber == designNo);

      setCurrentIndex(newIndex);
    },
    [allData, resolveProductByStyleNumber]
  );

  const handleCloseImageModal = React.useCallback(() => {
    setImageModal({ isOpen: false, imgUrl: null, designNo: null, selectedCartProduct: null });
    setCurrentIndex(currentIndex);
  }, []);

  const showDetails = React.useMemo(
    () => [
      {
        left: [
          { label: data?.styleNumber, Icon: resolveIcon(data.designClass?.toLowerCase(), variant), access: true },
          { label: `${data.diaWeight} Ct / ${data.diaPcs} Pcs`, Icon: resolveIcon("diamond", variant), tooltipTitle: "Diamond Weight / Diamond Pieces", access: true },
          { label: `${data.netWeight} g / ${data.grossWeight} g`, Icon: resolveIcon("goldbar", variant), tooltipTitle: "Gold Net Weight / Gold Gross Weight", access: true },
          {
            label: data.hasStock ? (data.duplicateStockCount ? `${data.duplicateStockCount} Articles` : `${data?.articleCount} ${data.articleCount === 1 ? "Article" : "Articles"}`) : "Out of Stock",
            Icon: resolveIcon(data.hasStock ? "in_stock" : "out_stock", variant),
            color: data.hasStock,
            access: true,
          },
          {
            label: formatDate(data?.styleDate),
            Icon: resolveIcon("date", variant),
            tooltipTitle: "Design Creation Date",
            access: true,
          },
        ],
      },
    ],
    [data, variant]
  );

  const hideDetails = React.useMemo(
    () => [
      {
        left: [
          { label: data?.styleNumber, Icon: getIcon(data?.designClass, variant), tooltipTitle: null, access: true },
          { label: data.view, Icon: getIcon("catalogView", variant), click: handleViewCartDetails ? () => handleViewCartDetails(data, "view") : null, tooltipTitle: null, access: true },
          { label: data.cart, Icon: getIcon("catalogCart", variant), click: handleViewCartDetails ? () => handleViewCartDetails(data, "cart") : null, tooltipTitle: null, access: true },
          {
            label: data.removeCart,
            Icon: getIcon("catalogRemoveCart", variant),
            click: handleViewCartDetails ? () => handleViewCartDetails(data, "removeCart") : null,
            tooltipTitle: null,
            access: true,
          },
          { label: data.clock, Icon: getIcon("catalogClock", variant), click: handleViewCartDetails ? () => handleViewCartDetails(data, "clock") : null, tooltipTitle: null, access: true },
        ],
      },
    ],
    [data, handleViewCartDetails, variant]
  );

  const handleNavigate = (data) => {
    if (location?.state?.aiIcon) {
      const defaultLocations = ["Headquarters Stock", "BBM Stock", "BBM Customer Stock", "BBS Stock", "BBS Customer Stock", "Idel Inventory"];
      const defaultStockStatus = "All";
      const isLocationChanged = Location.length !== defaultLocations.length || !defaultLocations.every((loc) => Location.includes(loc));

      const isStockStatusChanged = stockStatus !== defaultStockStatus;
      const isFilterApplied = isLocationChanged || isStockStatusChanged;
      if (isFilterApplied) {
        sessionStorage.setItem(
          "aiStockFilters",
          JSON.stringify({
            ...(isLocationChanged && { locationList: Location }),
            ...(isStockStatusChanged && { stockStatus: stockStatus }),
          })
        );
      }

      const query = isFilterApplied ? "?isFilter=true" : "";
      const newPath = `/aiStock/${data._id}/${data.styleNumber}${query}`;
      window.open(newPath, "_blank");
    } else {
      const name = [`SET - ${data.setNumber}`, `${data.styleNumber}`];
      const state = {
        ...location.state,
        name,
      };
      navigate(`${location.pathname}/${data.styleNumber}`, { state });
    }
  };

  useEffect(() => {
    setDetails(showDetails);
  }, []);

  const handleNextClick = () => {
    //console.log("check next -->", currentIndex);

    if (currentIndex < allData?.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      const nextItem = allData[newIndex];
      setImageModal((prev) => ({
        ...prev,
        isOpen: true,
        imgUrl: nextItem?.imageUrl ? nextItem?.imageUrl : null,
        designNo: nextItem?.styleNumber,
        selectedCartProduct: nextItem || resolveProductByStyleNumber(nextItem?.styleNumber),
      }));
    }
  };

  const handlePrevClick = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      const prevItem = allData[newIndex];

      setImageModal((prev) => ({
        ...prev,
        isOpen: true,
        imgUrl: prevItem?.imageUrl ? prevItem?.imageUrl : null,
        designNo: prevItem?.styleNumber,
        selectedCartProduct: prevItem || resolveProductByStyleNumber(prevItem?.styleNumber),
      }));
    }
  };

  useEffect(() => {
    if (allData?.length && data?.styleNumber) {
      const index = allData.findIndex((item) => item?.styleNumber === data.styleNumber);
      setCurrentIndex(index);
    }
  }, [allData]);

  const getTopLocation = (locations) => {
    if (!Array.isArray(locations)) return "defaultIcon";

    let topOnHand = { location: null, count: -1 };

    let topMemo = { location: null, count: -1 };

    locations.forEach((locObj) => {
      const [locationName] = Object.keys(locObj).filter((key) => key !== "status");
      const count = locObj[locationName];
      const status = locObj.status;

      if (status === "On Hand" && count > topOnHand.count) {
        topOnHand = { location: locationName, count };
      } else if (status === "Memo" && count > topMemo.count) {
        topMemo = { location: locationName, count };
      }
    });

    if (topOnHand.count > topMemo.count) {
      return topOnHand.location;
    } else if (topMemo.count > topOnHand.count) {
      return topMemo.location;
    } else {
      // tie
      return topOnHand.count !== -1 ? topOnHand.location : "defaultIcon"; // Prefer On Hand if tie
    }
  };

  const getLocationIcon = (location) => {
    const locationMap = {
      "BBM Stock": "bbm",
      "BBS Stock": "bbs",
      "BBM Customer Stock": "bbmC",
      "BBS Customer Stock": "bbsC",
      "Headquarters Stock": "hq",
      "Idel Inventory": "idl",
    };
    return locationMap[location] || "defaultIcon";
  };

  return (
    <>
      <Box
        sx={{
          width: "430px",
          height: "232px",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          // gap: "1rem",
          padding: "15px",
          backgroundColor: "#191818",
          color: "#fff",
          borderRadius: "10px",
          boxShadow: "0px 4px 10px rgba(0,0,0,0.2)",
          border: data.bestSeller ? "1px solid #C0C0C0" : "none",
        }}
      >
        {/* Left Content */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            height: "100%",
            // padding: "10px",
          }}
        >
          <Box
            className="right"
            onClick={(e) => {
              if (!data.hasStock) return;
              e.stopPropagation();
              handleNavigate(data);
            }}
            style={{ cursor: data.hasStock ? "pointer" : "not-allowed" }}
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              marginTop: "2px",
            }}
          >
            {details[0]?.left?.map(
              (detail, index) =>
                detail.access && (
                  <Box key={index} className={`content-new content-new-${index}`} display="flex" alignItems="center" sx={{ fontSize: "12px" }}>
                    {detail.click ? (
                      <IconButton disableFocusRipple disableRipple sx={{ p: 0 }} onClick={detail.click}>
                        {detail.Icon}
                      </IconButton>
                    ) : (
                      detail.Icon
                    )}
                    <Tooltip title={detail?.tooltipTitle} sx={{ marginTop: "2px" }}>
                      <Typography
                        className={`content-typo content-typo-${index}`}
                        ml={1}
                        whiteSpace="nowrap"
                        sx={{ color: detail.color ? "#50C878" : detail.label === "Out of Stock" ? "#E77B7B" : "white" }}
                      >
                        {detail.label}
                      </Typography>
                    </Tooltip>
                  </Box>
                )
            )}
          </Box>
        </Box>
        {/* Middle */}
        {/* <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "row",
            justifyContent: "end",
            alignItems: "start",
            height: "100%",
            padding: "10px",
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "start", alignItems: "start" }}> {getIcon(getLocationIcon(getTopLocation(data.location)))}</Box>
        </Box> */}

        {/* Right Content Image Side */}
        <Box
          // sx={{
          //   // flex: 0.4,
          //   width: "202px",
          //   height: "202px",
          //   display: "flex",
          //   alignItems: "center",
          //   justifyContent: "center",
          //   cursor: "pointer",
          // }}
          className={`left`}
          sx={{ width: "202px !important", height: "202px !important", position: "relative" }}
        >
          <SmallImage data={data} isBtn={isBtn} handleOpenImageModal={handleOpenImageModal} hasAccessToSeeWatermark={hasAccessToSeeWatermark} />
          {isBtn ? (
            <Button
              variant="contained"
              disabled={presentInCatalog ? presentInCatalog : false}
              sx={{
                backgroundColor: btnTilte === "Add" ? "#FFFFFF" : "#50C878",
                color: btnTilte === "Add" ? "#50C878 !important" : btnTilte === "Remove" ? "#E77B7B !important" : "#ffffff !important",
                border:
                  btnTilte === "Remove" || btnTilte === "Hide Details"
                    ? "1px solid #E77B7B"
                    : btnTilte === "Add" || btnTilte === "Show Details" || btnTilte === "Select"
                      ? "1px solid #50C878"
                      : "none",
                "&:hover": {
                  backgroundColor: btnTilte === "Add" || btnTilte === "Remove" || btnTilte === "Show Details" || btnTilte === "Hide Details" || btnTilte === "Select" ? "#FFFFFF" : "#50C878",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#FFFFFF !important",
                  color: "#444444 !important",
                  border: "1px solid #444444 !important",
                },
                width: btnWidth ? btnWidth : "110px !important",
                height: "40px !important",
                padding: "0px 20px !important",
                boxShadow: "0",
                fontFamily: "poppins",
              }}
              onClick={btnTilte === "Add" ? () => addtoCart(resolvedCartProduct) : () => removeCart(resolvedCartProduct)}
            >
              {btnTilte ? btnTilte : "Add"}
            </Button>
          ) : (
            ""
          )}
        </Box>
      </Box>

      <ImageModal
        open={imageModal.isOpen}
        handleClose={handleCloseImageModal}
        data={imageModal}
        selectedCartProducts={imageModal?.selectedCartProduct || resolvedCartProduct}
        hasAccessToSeeWatermark={hasAccessToSeeWatermark}
        nextClick={() => handleNextClick()}
        prevClick={() => handlePrevClick()}
        hasNext={!isLastItem}
        hasPrev={!isFirstItem}
      />
    </>
  );
};

export default React.memo(StockSetProductCard);
